<template>
  <v-container fluid justify-start>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Favorite Bulls'"
      v-bind:icon="'favorite'"
      v-bind:actionLink="$vuetify.breakpoint.name !== 'xs' ? 'Go To Bull Finder' : 'Bull Finder'"
      @action-link-click="actionLinkClicked"
    />

    <!-- If: No Favorite Bulls -->
    <template v-if="favorites.length === 0 && !isAdminy && !isLoading">
      <app-alert v-bind:alertType="'info'" v-bind:message="'You have no favorite bulls'" />
    </template>

    <!-- Else: Favorites Exist -->
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="favorites"
        :loading="isLoading"
        :no-data-text="noDataText"
        :search="search"
        mobile-breakpoint="500"
        >
        <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

        <!-- Header Toolbar -->
        <template v-slot:top>
          <v-toolbar dark :color="appColor_Medium" :height="isAdminy ? '64' : 'auto'">

            <!-- Customer Filter -->
            <v-toolbar-items class="pt-1">
              <bb-customer-dropdown
                v-if="isAdminy"
                v-model="userId"
                v-bind:profile="profile"
                @load="customersLoaded"
                @change="loadFavorites()"
                @error="customerError"
              />
            </v-toolbar-items>
            <v-spacer />

            <!-- Search (Large Device) -->
            <v-text-field
              v-if="(!isSmallDevice && isAdminy) || !isAdminy"
              v-model="search"
              :disabled="isLoading"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              clearable
              class="ml-3 mt-2"
            />
            
          </v-toolbar>

          <!-- Small Device Toolbar -->
          <v-toolbar
            v-if="isSmallDevice && isAdminy"
            light
            dark
            :color="appColor_Medium"
            >

            <!-- Search (Small Device) -->
            <v-text-field
              v-model="search"
              append-icon="search"
              :disabled="isLoading"
              label="Search"
              single-line
              hide-details
              dense
              clearable
            />
          </v-toolbar>
        </template>

        <template v-slot:item.sourceCosmosCode="{ item }">
          <v-chip small dark :color="item.sourceColourCode + ' lighten-2'" class="elevation-1">
            {{ item.sourceCosmosCode }}
          </v-chip>
        </template>

        <template v-slot:item.action="{ item }">
          <span class="text--center pr-4">
            <v-icon
              color="red lighten-1"
              @click="remove(item.id)"
              >
              remove_circle
            </v-icon>
          </span>
        </template>
      </v-data-table>
      
    </template>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        v-bind:errorTitle="errorTitle"
        v-bind:errorText="errorText"
        v-on:close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppAlert from '@/components/AppAlert'
import AppPageTitle from '@/components/AppPageTitle'
import AppError from '@/components/AppError'
import BbCustomerDropdown from '@/components/BbCustomerDropdown'
import { mapGetters } from 'vuex'
import { AppColor } from '@/config.js'

export default {
  name: 'FavoriteBulls',
  components: {
    AppPageTitle,
    AppAlert,
    AppError,
    BbCustomerDropdown
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Background: AppColor.Background,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,

      isLoading: true,
      search: '',
      favorites: [],
      headers: [
        {
          text: 'Source',
          value: 'sourceCosmosCode',
          align: 'left',
          width: '15%'
        },
        {
          text: 'VID',
          value: 'vid',
          align: 'left',
          width: '20%'
        },
        {
          text: 'Breed',
          value: 'breed',
          align: 'left',
          width: '35%'
        },
        {
          text: 'Year',
          value: 'birthYear',
          align: 'left',
          width: '15%'
        },
        {
          text: 'Remove',
          value: 'action',
          align: 'center',
          width: '15%'
        }
      ],

      // Adminy stuff
      userId: null,
      userCustomers: [],

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null      
    }
  },
  computed: {
    ...mapGetters(['profile', 'currentBullSale']),
    noDataText () {
      return this.isLoading
        ? 'One moment...'
        : 'No favorite bulls found'
    },
    isAdmin () {
      return this.profile.roles !== null && this.profile.roles.includes('Admin')
    },
    isBreeder () {
      return this.profile.roles !== null && this.profile.roles.includes('Breeder')
    },
    isAdminy () {
      return this.isAdmin || this.isBreeder
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name  === 'sm'
    },
    isCurrentUser () {
      return this.profile.id === this.userId
    },
    isSaleMode () {
      return this.$route.query.saleId !== undefined
    }
  },
  methods: {
    remove (id) {
      appService.deleteFavoriteBull(id).then(() => {
        var favs = this.favorites.filter(function (value) {
          return value.id === id
        })
        if (favs.length > 0) {
          var index = this.favorites.indexOf(favs[0])
          this.$delete(this.favorites, index)
        }
      })
    },

    loadData () {
      if (this.profile.id === undefined) this.$router.push('/')

      this.isLoading = true
      this.userId = this.profile.id

      if (!this.isAdminy) {
        this.loadFavorites()
      } else {
        // default user_id might have been passed in
        if (this.isAdminy && this.$route.params.user_id !== undefined) {
          this.userId = this.$route.params.user_id
        }
      }
    },

    loadFavorites () {
      this.isLoading = true
      appService.getFavoriteBulls(this.userId).then(data => {
        if (data.errorSeverity > 0) {
          this.errorMessage = data.errorMessage
          this.errorDialog = true
        } else {
          this.favorites = data.favoriteBulls
        }
        this.isLoading = false
      })
    },

    // event handlers
    customersLoaded ({ customers }) {
      this.userCustomers = customers
      this.loadFavorites()
    },

    customerError ({ error }) {
      if (error.severity > 0) {
        this.isError = true
        this.errorTitle = 'Customer Load Error'
        this.errorText = error.message
        this.errorDialog = true
      }
    },

    actionLinkClicked () {
      const path = this.isCurrentUser
        ? '/bullFinder'
        : `/bullFinder/${this.userId}`

      if (this.isSaleMode) {
        this.$router.push({ path: path, query: {
          saleId: this.currentBullSale.id
        }}).catch(() => {});
      } else {
        this.$router.push({ path: path }).catch(() => {});
      }
    }
  },
  created () {
    this.loadData()
  }
}
</script>

